import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SeparatorLayout } from "@/app/components/separator-layout";
import { AppDateFormats, formatDate } from "@/app/lib/format-date";
import { useModalContext } from "@/app/modal-provider/modal-context";
import {
  ChangeRequestAction,
  ChangeRequestStatus,
  OrderByDirection,
  TradeContractChangeRequest,
  useAcceptTradeContractChangeRequestMutation,
  useRejectTradeContractChangeRequestMutation,
  useTradeContractChangeRequestHistoryQuery,
  useWithdrawTradeContractChangeRequestMutation,
} from "@/app/types/generated/graphql";

import {
  ChangeRequestActionMutationMap,
  ChangeRequestModalContent,
  UpdateChangeRequestProps,
  UseRequestDateChangeTableProps,
} from "./request-date-change-table.types";

export const useRequestDateChangeTable = ({
  tradeContractId,
  refetchTradeContract,
}: UseRequestDateChangeTableProps) => {
  const { t } = useTranslation();

  const { showConfirmationModal, showForwardTradeChangeRequestModal } = useModalContext();

  const {
    data,
    loading: changeRequestHistoryLoading,
    refetch,
  } = useTradeContractChangeRequestHistoryQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      input: {
        tradeContractId,
        sortOrderInputs: [
          {
            field: "requestedOn",
            sortOrder: OrderByDirection.Desc,
          },
        ],
      },
    },
  });

  const [acceptTradeContractChangeRequest] = useAcceptTradeContractChangeRequestMutation();

  const [withdrawTradeContractChangeRequest] = useWithdrawTradeContractChangeRequestMutation();

  const [rejectTradeContractChangeRequest] = useRejectTradeContractChangeRequestMutation();

  const confirmChangeRequest = useCallback(
    async (changeRequest: TradeContractChangeRequest, currentAction: ChangeRequestAction) => {
      const mutationMap: ChangeRequestActionMutationMap = {
        [ChangeRequestAction.Accept]: acceptTradeContractChangeRequest,
        [ChangeRequestAction.Withdraw]: withdrawTradeContractChangeRequest,
        [ChangeRequestAction.Reject]: rejectTradeContractChangeRequest,
      };
      const mutationFn = mutationMap[currentAction];
      if (!mutationFn) return;

      await mutationFn({
        variables: {
          input: {
            tradeContractId,
            changeRequestId: changeRequest.changeRequestId,
          },
        },
      });
      await refetch();
      if (currentAction === ChangeRequestAction.Accept) {
        await refetchTradeContract?.();
      }
    },
    [
      tradeContractId,
      refetchTradeContract,
      refetch,
      acceptTradeContractChangeRequest,
      withdrawTradeContractChangeRequest,
      rejectTradeContractChangeRequest,
    ],
  );

  const onChangeRequestAction = useCallback(
    (changeRequest: TradeContractChangeRequest, currentAction: ChangeRequestAction) => {
      const modalContent = ChangeRequestModalContent[currentAction];
      if (!modalContent) return;
      const { title, subtitle, successMessage, errorMessage } = modalContent;

      showConfirmationModal({
        title,
        subtitle,
        onSubmit: () => confirmChangeRequest(changeRequest, currentAction),
        messages: {
          successMessage: t(successMessage),
          errorMessage: t(errorMessage),
        },
        content: (
          <SeparatorLayout
            contentStyle={{
              width: "40%",
            }}
            data={{
              "Payment Date": formatDate(changeRequest.paymentDate, {
                formatStyle: AppDateFormats.AbbreviatedMonthFormat,
                noOffset: true,
                noTZ: true,
              }),
              "Delivery Date (On or Before)": formatDate(changeRequest.deliveryDate, {
                formatStyle: AppDateFormats.AbbreviatedMonthFormat,
                noOffset: true,
                noTZ: true,
              }),
            }}
          />
        ),
        modalMaxWidth: "800px",
      });
    },
    [showConfirmationModal, confirmChangeRequest, t],
  );

  const allowAddingNewRequest = useMemo(
    () =>
      !changeRequestHistoryLoading &&
      !data?.tradeContractChangeRequestHistory?.some((request) =>
        [ChangeRequestStatus.Open, ChangeRequestStatus.Accepted, ChangeRequestStatus.Rejected].includes(request.status),
      ),
    [data?.tradeContractChangeRequestHistory, changeRequestHistoryLoading],
  );

  const hideActionColumn = useMemo(
    () =>
      (data?.tradeContractChangeRequestHistory ?? []).some((request) =>
        [ChangeRequestStatus.Accepted, ChangeRequestStatus.Rejected].includes(request.status),
      ),
    [data?.tradeContractChangeRequestHistory],
  );

  const reloadChangeRequestHistory = useCallback(async () => {
    await refetchTradeContract?.();
    await refetch();
  }, [refetch, refetchTradeContract]);

  const onUpdateOrCounter = useCallback(
    ({ paymentDate, deliveryDate, mode }: UpdateChangeRequestProps) => {
      if (!paymentDate || !deliveryDate || !tradeContractId) return;
      showForwardTradeChangeRequestModal({
        initialData: {
          tradeContractId,
          paymentDate,
          deliveryDate,
        },
        mode,
      });
    },
    [tradeContractId, showForwardTradeChangeRequestModal],
  );

  return {
    loading: changeRequestHistoryLoading,
    tableData: data?.tradeContractChangeRequestHistory ?? [],
    reloadChangeRequestHistory,
    allowAddingNewRequest,
    hideActionColumn,
    actions: { onChangeRequestAction, onUpdateOrCounter },
  };
};
